/**
 * Persistent Installation ID
 *
 * Generates a UUID on first app launch and persists it in AsyncStorage.
 * Used as `X-Device-Id` header to uniquely identify each app installation
 * for device-aware in-app message dismissals.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { uuidv7 } from 'uuidv7';
import { STORAGE_KEYS } from './storageKeys';

let cached: string | null = null;

/**
 * Load (or generate) the installation ID and cache it in memory.
 * Call once at app startup before any API requests.
 */
export async function initInstallationId(): Promise<string> {
  if (cached) return cached;

  const stored = await AsyncStorage.getItem(STORAGE_KEYS.DEVICE_INSTALLATION_ID);
  if (stored) {
    cached = stored;
    return stored;
  }

  const id = uuidv7();
  await AsyncStorage.setItem(STORAGE_KEYS.DEVICE_INSTALLATION_ID, id);
  cached = id;
  return id;
}

/**
 * Synchronous getter — returns the cached ID or 'unknown' if not yet initialized.
 * Safe to use in Axios request interceptors (which are synchronous).
 */
export function getInstallationIdSync(): string {
  return cached ?? 'unknown';
}
